import React, {useEffect, useState} from 'react';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import LinearProgress from '@mui/material/LinearProgress';

const SLOW_AFTER_MS = 30000

// The loading is shown in two stages:
// - LoadingBackdrop, full screen, only until the map and the data needed to display the first screen are there
// - LoadingBar, a thin bar that does not block anything, while the rest of the data arrives (the controls that
//   still wait for their data are greyed out meanwhile)

// Full-screen loader that blocks any click until the first screen can be displayed.
// Shows a determinate progress ring (done / total steps, as a percentage) with a message and a step label under it.
// Same look as the static splash in public/index.html, which it takes over from (hence no fade-in).
// When a step failed (error), or when the loading takes long (slowMessage), it says so and offers to retry (reloads the page).
export default function LoadingBackdrop({open, done, total, message, step, error, slowMessage, retryLabel, continueLabel, onContinue}) {
    const [slow, setSlow] = useState(false)
    useEffect(() => {
        if (!open) return
        const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS)
        return () => clearTimeout(timer)
    }, [open])

    const clampedDone = Math.min(done, total)
    const percent = total > 0 ? Math.round(100 * clampedDone / total) : 0

    return (
        <Backdrop
            sx={{color: '#fff', zIndex: 2000, flexDirection: 'column', gap: '24px'}}
            open={open}
            transitionDuration={{enter: 0, exit: 225}}
        >
            <Box sx={{position: 'relative', display: 'inline-flex'}}>
                {/* faint track behind the progress ring */}
                <CircularProgress
                    variant="determinate"
                    value={100}
                    size={80}
                    thickness={3.6}
                    sx={{color: 'rgba(255, 255, 255, 0.25)'}}
                />
                <CircularProgress
                    variant="determinate"
                    value={percent}
                    size={80}
                    thickness={3.6}
                    color="inherit"
                    sx={{position: 'absolute', left: 0}}
                />
                <Box sx={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <span className={'loader-percent'}>{percent}%</span>
                </Box>
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px'}}>
                <div className={'loader-message'}>{error || message}</div>
                <div className={'loader-step'}>{step ? `${step} (${clampedDone}/${total})` : ''}</div>
                {!error && slow && <div className={'loader-step'}>{slowMessage}</div>}
            </Box>
            {(error || slow) &&
                <Box sx={{display: 'flex', gap: '12px'}}>
                    <Button variant="outlined" color="inherit" onClick={() => window.location.reload()}>{retryLabel}</Button>
                    {error && <Button variant="text" color="inherit" onClick={onContinue}>{continueLabel}</Button>}
                </Box>
            }
        </Backdrop>
    )
}

// Thin progress bar at the top of the screen, with a label at the bottom: the map and the controls stay usable meanwhile.
// When a step failed (error), it says so and offers to retry (reloads the page) or to dismiss the message.
export function LoadingBar({open, done, total, label, error, retryLabel, continueLabel, onContinue}) {
    const clampedDone = Math.min(done, total)
    const percent = total > 0 ? Math.round(100 * clampedDone / total) : 0

    return (
        <Fade in={open} timeout={{enter: 0, exit: 400}} unmountOnExit>
            <Box className={'loading-bar'} sx={{position: 'fixed', inset: 0, zIndex: 1500, pointerEvents: 'none'}}>
                <LinearProgress
                    variant="determinate"
                    value={error ? 100 : percent}
                    color={error ? 'error' : 'primary'}
                    sx={{height: '3px'}}
                />
                <Box className={'loading-bar-label'} sx={{position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)',
                    pointerEvents: error ? 'auto' : 'none', display: 'flex', alignItems: 'center', gap: '8px',
                    width: 'max-content', maxWidth: '90vw', padding: '2px 14px', borderRadius: '14px', color: '#fff',
                    backgroundColor: error ? 'rgba(160, 30, 30, 0.9)' : 'rgba(0, 0, 0, 0.55)'}}>
                    <div className={'loader-step'}>{error || (label ? `${label} (${clampedDone}/${total})` : '')}</div>
                    {error &&
                        <React.Fragment>
                            <Button size="small" variant="outlined" color="inherit" onClick={() => window.location.reload()}>{retryLabel}</Button>
                            <Button size="small" variant="text" color="inherit" onClick={onContinue}>{continueLabel}</Button>
                        </React.Fragment>
                    }
                </Box>
            </Box>
        </Fade>
    )
}
